import { themeAtom } from "../../../shared/atoms";
import type { AdminPnlSyncModalProps } from "../../models/admin-pnl.models";
import { adminStyles } from "../../styles/admin.styles";
import { Dialog } from "@headlessui/react";
import { useAtomValue } from "jotai";
import { useRef } from "react";
import { twMerge } from "tailwind-merge";

const AdminPnlSyncModal = ({ preview, syncing, errorMessage, onClose, onConfirm }: AdminPnlSyncModalProps) => {
  const theme = useAtomValue(themeAtom);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  return (
    <Dialog
      as="div"
      open
      initialFocus={cancelRef}
      onClose={() => {
        if (!syncing) onClose();
      }}
      data-test="admin-pnl-sync-modal"
      className="fixed inset-0 z-50"
    >
      <Dialog.Overlay className="fixed inset-0 bg-black/45" />
      <div className="fixed inset-0 flex items-center justify-center px-3 py-4">
        <Dialog.Panel
          className={twMerge(
            "max-h-[90vh] w-full max-w-xl overflow-y-auto bg-white px-5 py-5 shadow-2xl dark:bg-gray-800",
            theme.home.tailwind.textDefault,
          )}
        >
          <Dialog.Title className="text-xl font-semibold">Sync vessel name?</Dialog.Title>
          <Dialog.Description className="mt-3 text-sm leading-6">
            Update the saved vessel names for contract {preview.target.tcCode}, voyage {preview.target.voyageNo}, using
            the active vessel name: <strong>{preview.row.CURRENT_NAME}</strong>.
          </Dialog.Description>
          <p className="mt-2 text-xs opacity-75">
            Database: {preview.row.DATABASE_NAME} · Vessel code: {preview.row.VESSEL_CODE} · IMO:{" "}
            {preview.row.IMO_NUMBER}
          </p>
          <table className="mt-4 w-full text-left text-sm" data-test="admin-pnl-sync-changes">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-600">
                <th className="py-2">Records</th>
                <th>Saved name</th>
                <th>Current name</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {preview.changes.map((change, index) => (
                <tr key={index} className="border-b border-gray-100 dark:border-gray-700">
                  <td className="py-2 capitalize">{change.table}</td>
                  <td>{change.fromName ?? "Empty"}</td>
                  <td>{change.toName}</td>
                  <td>{change.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-sm">
            This updates {preview.approvalCount} approval record(s) and {preview.summaryCount} summary record(s).
          </p>
          {errorMessage && (
            <p role="alert" data-test="admin-pnl-sync-error" className="mt-4 text-sm text-[#BF0002]">
              {errorMessage}
            </p>
          )}
          <div className="mt-6 flex justify-between gap-3">
            <button
              type="button"
              data-test="admin-pnl-sync-confirm"
              disabled={syncing || Boolean(errorMessage)}
              onClick={onConfirm}
              className={twMerge(
                adminStyles.button.primary,
                adminStyles.button.live,
                adminStyles.button.disabled,
                "w-auto",
              )}
            >
              {syncing ? "Syncing..." : "Confirm sync"}
            </button>
            <button
              ref={cancelRef}
              type="button"
              data-test="admin-pnl-sync-cancel"
              disabled={syncing}
              onClick={onClose}
              className={twMerge(
                adminStyles.button.primary,
                adminStyles.button.disabled,
                "w-auto border border-green text-green",
              )}
            >
              Cancel
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default AdminPnlSyncModal;
